import { useCallback, useState } from 'react'
import { notification } from '@surecompanies/core_components'
import { extractError } from '../../api/extract-error'
import { getAllUsers } from '../../api/user-invitation.api'
import type { GetAllUsersRequestDto } from '../../api/dto/user-invitation.dto'
import type { ExportFormat } from '../ClientBillingProfileManagement/bulkUpload.excel'
import { downloadRosterExport } from './rosterExport'

interface UseRosterExportParams {
  /** The filters in force on the table, so the file holds what is on screen. */
  filters: GetAllUsersRequestDto
}

/**
 * Downloads the filtered roster as a spreadsheet. The rows come from the server
 * rather than the table, which only ever holds one page.
 */
export const useRosterExport = ({ filters }: UseRosterExportParams) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const handleOpen = useCallback(() => setModalOpen(true), [])
  const handleClose = useCallback(() => setModalOpen(false), [])

  const handleDownload = useCallback(
    async (_scope: 'FUNDING_MANAGERS', format: ExportFormat) => {
      setIsExporting(true)
      try {
        const { data, total } = await getAllUsers(filters)
        downloadRosterExport(data, format)
        if (total > data.length) {
          notification.warning({
            message: `Exported the first ${data.length} of ${total} users`,
            description: 'Narrow the filters to export the rest.',
          })
        }
        setModalOpen(false)
      } catch (error) {
        notification.error({ message: extractError(error, 'Failed to export') })
      } finally {
        setIsExporting(false)
      }
    },
    [filters]
  )

  return { modalOpen, isExporting, handleOpen, handleClose, handleDownload }
}
